// Логика магазина без DOM (E12-T11, E12-T22): вкладки ux/08 §1.1, ориентир «≈ ★ · ≈ ₽» (правило 2), пакет под
// нехватку талонов (§2.1), товары за талоны. Чисел баланса здесь нет: цены, пределы, курс рубля, термос, лавка и
// флаг платных ящиков приходят в `GET /shop` (Р-86). Чего сервер не прислал, клиент не показывает и не придумывает.
import type { Api, Grant, ResponseOf } from '@garages/contract';
import { num } from '../i18n/format.js';

export type TabId = 'talons' | 'sets' | 'shop' | 'boxes' | 'energy' | 'boosts' | 'looks' | 'misc';

/** Порядок и подписи вкладок — ux/08 §1.1 (8 вкладок, Р-49). */
export const STORE_TABS: readonly { id: TabId; key: string }[] = [
  { id: 'talons', key: 'SHOP-TAB-TALONS' }, { id: 'sets', key: 'CL-STORE-TAB-SETS' }, { id: 'shop', key: 'CL-STORE-TAB-SHOP' },
  { id: 'boxes', key: 'SHOP-TAB-BOXES' }, { id: 'energy', key: 'CL-STORE-TAB-ENERGY' }, { id: 'boosts', key: 'CL-STORE-TAB-BOOSTS' },
  { id: 'looks', key: 'CL-STORE-TAB-LOOKS' }, { id: 'misc', key: 'CL-STORE-TAB-MISC' },
];

/** Вкладки, которые видит игрок: «Ящиков» нет, пока `shop.paid_boxes = false` (ux/08 §1.3, ВР-10) или страна запрещает. */
export function visibleTabs(o: { paidBoxes: boolean; boxesGeoHidden?: boolean }): TabId[] {
  return STORE_TABS.map((t) => t.id).filter((id) => id !== 'boxes' || (o.paidBoxes && !o.boxesGeoHidden));
}

export interface GoodLimit { per: 'day' | 'week' | 'season' | 'lifetime'; total: number; used: number }

/** Товар за талоны, как его видит клиент. */
export interface TalonGood {
  productId: string;
  section: TabId | 'pass';
  /** Цена следующей покупки; null — сервер цену не прислал (товар не продаётся). */
  priceTalons: number | null;
  limit?: GoodLimit;
  /** Почему не продаётся: `not_yet` (система ещё не готова, Р-65), `paid_boxes_off`, `raid_off`, `raid_failed`. */
  blocked?: string;
  /** Сколько открытий до гарантии «Ящика мастера»: эпической и легендарной (ux/08 §1.3). */
  pityLeft?: number;
  pityLegendary?: number;
  /** Конкретный предмет (лавка Семёныча), если сервер его выбрал, и его характеристики до покупки (Р-88). */
  defId?: string;
  stats?: Partial<Record<'attack' | 'hp' | 'def' | 'crit' | 'critMult' | 'dodge', number>>;
  purchased?: boolean;
  refreshAt?: string;
  firstFree?: boolean;
  /** Что в ящике: ID для `GET /boxes/{id}/odds`. */
  boxDefId?: string;
  qty?: number;
}

export interface ThermosState { nextPriceTalons: number | null; leftToday: number; perDay: number }

export interface StoreExtras {
  goods: TalonGood[];
  rubPerStar: number | null;
  paidBoxes: boolean;
  thermos: ThermosState | null;
  talonsDebt: number;
}

type ShopResp = ResponseOf<Api['getShop']>;
type GoodView = NonNullable<ShopResp['talonGoods']>[number];

/** Ящики за талоны: что открывать после покупки и сколько (ID товаров — Р-49). */
const BOXES: Record<string, { boxDefId: string; qty: number }> = {
  'GOOD-BOX-MASTER': { boxDefId: 'BOX-MASTER', qty: 1 }, 'GOOD-BOX-MASTER-10': { boxDefId: 'BOX-MASTER', qty: 10 },
};
const SECTIONS = new Set<string>(['talons', 'sets', 'shop', 'boxes', 'energy', 'boosts', 'looks', 'misc', 'pass']);

/** Товар за талоны из `GET /shop` (Р-86) → вид клиента. Долг и предел исчерпан считает buyState, не флаг сервера. */
export function fromView(g: GoodView): TalonGood | null {
  if (!SECTIONS.has(g.section)) return null;
  const out: TalonGood = { productId: g.productId, section: g.section as TalonGood['section'], priceTalons: g.priceTalons };
  const l = g.limit;
  if (l?.perDay) out.limit = { per: 'day', total: l.perDay, used: g.used };
  else if (l?.perWeek) out.limit = { per: 'week', total: l.perWeek, used: g.used };
  else if (l?.lifetime) out.limit = { per: 'lifetime', total: l.lifetime, used: g.used };
  if (g.status === 'blocked' && (g.reason === 'not_yet' || g.reason === 'paid_boxes_off')) out.blocked = g.reason;
  if (g.status === 'blocked' && g.reason === 'limit' && !out.limit) out.purchased = true;
  if (g.pityLeft) { out.pityLeft = g.pityLeft.epic; out.pityLegendary = g.pityLeft.legendary; }
  if (BOXES[g.productId]) Object.assign(out, BOXES[g.productId]);
  if (g.productId === 'GOOD-RENAME' && g.priceTalons === 0) out.firstFree = true;
  return out;
}

/** Всё, что магазину нужно сверх пакетов за ★, — из ответа `GET /shop` (Р-86). Чего сервер не прислал — не показываем. */
export function storeExtras(shop: ShopResp | null, features: Record<string, boolean>): StoreExtras {
  const lavka = shop?.lavka;
  const goods = (shop?.talonGoods ?? [])
    .filter((g) => !(lavka && g.productId.startsWith('GOOD-SEMYONYCH')))
    .map(fromView).filter((g): g is TalonGood => !!g);
  if (lavka) {
    for (const it of lavka.items) {
      goods.push({ productId: it.productId, section: 'shop', priceTalons: it.priceTalons, defId: it.defId, purchased: it.purchased, refreshAt: lavka.refreshAt, ...(it.stats ? { stats: it.stats } : {}), limit: { per: 'week', total: 1, used: it.purchased ? 1 : 0 } });
    }
  }
  return {
    goods,
    rubPerStar: shop?.rubPerStar ?? null,
    paidBoxes: shop?.paidBoxes ?? !!features['shop.paid_boxes'],
    thermos: shop?.thermos ?? null,
    talonsDebt: shop?.talonsDebt ?? 0,
  };
}

// ---------------- ориентир цены (правило 2, E12-T22) ----------------

export interface Hint { stars: number | null; rub: number | null }

/** У цены в ★ — только рубли: «≈ {r} ₽». */
export function starsHint(xtr: number, rub: number | null): Hint {
  return { stars: null, rub: rub ? Math.round(xtr * rub) : null };
}

/**
 * У цены в талонах — «≈ {n} ★ · ≈ {r} ₽». Курс — базовый «Горсти» (2 талона за ★, правило 4): так ориентир не
 * занижает цену в звёздах. ★ — с округлением вверх, ₽ — до рубля.
 */
export function talonsHint(talons: number, baseRate: number, rub: number | null): Hint {
  const stars = baseRate > 0 ? Math.ceil(talons / baseRate) : null;
  return { stars, rub: stars !== null && rub ? Math.round(stars * rub) : null };
}

// ---------------- нехватка талонов (§2.1, E12-T22) ----------------

export interface PackLike { productId: string; priceXtr: number; grant: Grant; badge: string | null; blocked?: string | undefined }

const talonsOf = (p: PackLike) => p.grant.currencies?.talons ?? 0;

/**
 * Наименьший пакет, покрывающий нехватку (стартовый — только пока доступен и тоже подходит). Если ни один не
 * покрывает — самый крупный. `stars` — «не хватает N★»: нехватка по курсу выбранного пакета, с округлением вверх.
 */
export function coverPack<P extends PackLike>(deficit: number, packs: P[]): { pack: P; stars: number } | null {
  if (deficit <= 0) return null;
  const usable = packs.filter((p) => talonsOf(p) > 0 && p.priceXtr > 0).sort((a, b) => a.priceXtr - b.priceXtr || talonsOf(b) - talonsOf(a));
  if (!usable.length) return null;
  const pack = usable.find((p) => talonsOf(p) >= deficit) ?? usable.reduce((m, p) => (talonsOf(p) > talonsOf(m) ? p : m));
  return { pack, stars: Math.ceil(deficit / (talonsOf(pack) / pack.priceXtr)) };
}

// ---------------- покупка за талоны (§2.1, §2.2) ----------------

export type BuyState =
  | { kind: 'ok'; price: number; hold: boolean }
  | { kind: 'lack'; price: number; deficit: number }
  | { kind: 'debt' }
  | { kind: 'limit'; per: GoodLimit['per'] }
  | { kind: 'bought' }
  | { kind: 'blocked'; reason: string }
  | { kind: 'noprice' };

/** Порог удержания кнопки 1 с — покупки дороже 1 000 талонов (§2.2, `[старт]`). */
export const HOLD_OVER_TALONS = 1000;

export function buyState(g: Pick<TalonGood, 'priceTalons' | 'limit' | 'blocked' | 'purchased' | 'firstFree'>, talons: number, debt: boolean): BuyState {
  if (g.blocked) return { kind: 'blocked', reason: g.blocked };
  if (g.purchased) return { kind: 'bought' };
  if (g.limit && g.limit.used >= g.limit.total) return g.limit.per === 'lifetime' ? { kind: 'bought' } : { kind: 'limit', per: g.limit.per };
  if (g.priceTalons === null) return { kind: 'noprice' };
  if (debt && g.priceTalons > 0) return { kind: 'debt' };
  if (talons < g.priceTalons) return { kind: 'lack', price: g.priceTalons, deficit: g.priceTalons - Math.max(0, talons) };
  return { kind: 'ok', price: g.priceTalons, hold: g.priceTalons > HOLD_OVER_TALONS };
}

/** Есть ли долг по талонам (tech/05 §8): баланс < 0 или явный `talonsDebt`. */
export function inDebt(w: { talons: number; talonsDebt?: number | undefined } | null): boolean {
  return !!w && (w.talons < 0 || (w.talonsDebt ?? 0) > 0);
}

/** Параметры строки с числом талонов: `n` — для склонения (ICU plural), `s` — число с разрядами для показа. */
export function tn(n: number): { n: number; s: string } {
  return { n, s: num(n) };
}

/** Шанс для MOD-ODDS: два знака после запятой, «< 0,01%» вместо нуля (ux/03 §9). */
export function pct(chance: number): string {
  if (chance > 0 && chance < 0.0001) return '< 0,01%';
  return `${(chance * 100).toFixed(2).replace('.', ',')}%`;
}

/** Сумма строк таблицы шансов = 100% (в пределах округления сервера). */
export function oddsSumOk(rows: { chance: number }[]): boolean {
  return Math.abs(rows.reduce((s, r) => s + r.chance, 0) - 1) < 1e-6;
}
